import { useState } from 'react';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { listDatasetsQuery } from '@gonfalon/rest-api/queries/datasets';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTrigger,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import type { DatasetOption } from './types';

export interface DatasetPickerProps {
  /** Project key used to list the project's datasets. */
  projectKey: string;
  /** Phase 1: the host logs the chosen dataset; later it promotes the span into it. */
  onSelect: (dataset: DatasetOption) => void;
}

/**
 * A "+ Add to dataset" trigger that opens a searchable list of the project's datasets. Selecting
 * one calls `onSelect`. Built entirely from LaunchPad components; the dataset collection comes from
 * the datasets API, which filters server-side via its `filter` query param (so we don't pass a
 * client-side `filter` to the Autocomplete).
 */
export const DatasetPicker = ({ projectKey, onSelect }: DatasetPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  // Only hit the API once the picker is opened; the server filters by the debounced search term.
  const { data, isFetching } = useQuery({
    ...listDatasetsQuery({ projKey: projectKey, query: { filter: debouncedSearch || undefined } }),
    enabled: isOpen,
  });

  const options: DatasetOption[] = (data?.datasets ?? []).map((dataset) => ({
    id: dataset.id,
    name: dataset.name ?? dataset.filename,
  }));

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button aria-label="Add to dataset" variant="default">
        <Icon name="add" size="small" />
        Add to dataset
      </Button>
      <Popover placement="bottom start" style={{ minWidth: 280 }}>
        <Dialog aria-label="Add to dataset">
          <Autocomplete inputValue={search} onInputChange={setSearch}>
            <SearchField aria-label="Search datasets" autoFocus>
              <Group>
                <Icon name="search" size="small" />
                <Input placeholder="Search datasets" />
              </Group>
            </SearchField>
            <ListBox
              aria-label="Datasets"
              items={options}
              renderEmptyState={() => (
                <div style={{ padding: '0.5rem' }}>{isFetching ? 'Loading datasets…' : 'No datasets found'}</div>
              )}
              onAction={(key) => {
                const dataset = options.find((option) => option.id === String(key));
                if (dataset) {
                  onSelect(dataset);
                  setIsOpen(false);
                }
              }}
            >
              {(dataset) => (
                <ListBoxItem id={dataset.id} textValue={dataset.name}>
                  {dataset.name}
                </ListBoxItem>
              )}
            </ListBox>
          </Autocomplete>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
